import { CheckCircle2, Loader2, Maximize2, Minimize2, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';

import type { AnalyticsWorkflowStatus } from '../types/analytics-sync.types';

/** One connector's live progress line inside the toast. */
export interface AnalyticsSyncToastRow {
  connectorCode: string;
  status: AnalyticsWorkflowStatus;
  /** 0–100 — the watcher resolves a concrete value (0 before first progress). */
  percent: number;
}

interface AnalyticsSyncToastProps {
  experimentName: string;
  rows: AnalyticsSyncToastRow[];
  /** Averaged 0–100 across all rows — drives the header bar. */
  overallPercent: number;
  /** When true the per-connector breakdown is hidden and only the combined bar shows. */
  collapsed: boolean;
  /** Toggles the collapsed state. Owned by the watcher so it survives re-polls. */
  onToggleCollapse: () => void;
}

/** Per-row status glyph: spinner while running, check on done, cross on failure. */
function StatusIcon({ status }: { status: AnalyticsWorkflowStatus }) {
  if (status === 'COMPLETED') {
    return <CheckCircle2 aria-hidden="true" className="text-success h-3.5 w-3.5 shrink-0" />;
  }
  if (status === 'FAILED' || status === 'CANCELLED') {
    return <XCircle aria-hidden="true" className="text-error h-3.5 w-3.5 shrink-0" />;
  }
  return <Loader2 aria-hidden="true" className="text-primary h-3.5 w-3.5 shrink-0 animate-spin" />;
}

/** Inline progress bar mirroring the `processing-progress-card` pattern
 *  (`--surface-progress-track` track + `bg-primary` fill). */
function MiniBar({ percent }: { percent: number }) {
  const pct = Math.max(0, Math.min(100, percent));
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      className="h-1.5 w-full overflow-hidden rounded-full"
      style={{ backgroundColor: 'var(--surface-progress-track)' }}
    >
      <div
        className="bg-primary h-full rounded-full transition-[width] duration-300 ease-out"
        style={{ width: `${String(pct)}%` }}
      />
    </div>
  );
}

/**
 * Non-blocking progress toast body for an in-flight analytics sync. Renders the
 * combined progress plus one row per connector; the app-level watcher re-emits
 * this with the same toast id on every poll so it updates in place.
 */
export function AnalyticsSyncToast({
  experimentName,
  rows,
  overallPercent,
  collapsed,
  onToggleCollapse,
}: AnalyticsSyncToastProps) {
  const { t } = useTranslation('experiments');
  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-foreground text-sm font-semibold">
          {t('detail.syncAnalytics.toast.running')}
        </span>
        <div className="flex items-center gap-1.5">
          <span className="text-primary text-xs font-semibold">{Math.round(overallPercent)}%</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t(
              collapsed
                ? 'detail.syncAnalytics.toast.expand'
                : 'detail.syncAnalytics.toast.collapse',
            )}
            onClick={onToggleCollapse}
            className="text-muted-foreground hover:text-foreground h-6 w-6 shrink-0 cursor-pointer"
          >
            {collapsed ? (
              <Maximize2 aria-hidden="true" className="h-3.5 w-3.5" />
            ) : (
              <Minimize2 aria-hidden="true" className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>
      <p className="text-muted-foreground truncate text-xs">{experimentName}</p>
      <MiniBar percent={overallPercent} />
      {/* Per-connector breakdown — hidden when collapsed to keep the toast narrow. */}
      {!collapsed && (
        <ul className="mt-1 flex flex-col gap-1.5">
          {rows.map((row) => (
            <li key={row.connectorCode} className="flex items-center gap-2 text-xs">
              <StatusIcon status={row.status} />
              <span className="text-foreground w-28 shrink-0 truncate">
                {t(`detail.syncAnalytics.sources.${row.connectorCode}`, {
                  defaultValue: row.connectorCode,
                })}
              </span>
              <span className="flex-1">
                <MiniBar percent={row.percent} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
